import { Injectable, effect, untracked } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { MyPermissions, RoutePermissionData } from '../../models/permission.model';
import { PermissionStore } from './permission.store';

/** API PermissionHub.Path, on the API host. */
const HUB_URL = new URL('/hubs/permissions', environment.BaseURL).toString();
/** API PermissionHub.PermissionsUpdated. */
const PERMISSIONS_UPDATED = 'PermissionsUpdated';
/** Retry delay after automatic reconnect gives up or the first start fails. */
const RESTART_DELAY_MS = 15000;

/**
 * Keeps PermissionStore current over SignalR while an admin is signed in.
 * The API pushes the full permission payload on connect (so changes missed while offline are recovered)
 * and whenever the admin's role or its permissions change. The page the admin is on is re-checked
 * after every update, so lost access moves them to a page they may open.
 * Created at startup (app-module provideAppInitializer).
 */
@Injectable({ providedIn: 'root' })
export class PermissionRealtimeService {
  private connection: HubConnection | null = null;
  private restartTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly permissionStore: PermissionStore,
    private readonly router: Router
  ) {
    effect(() => {
      const signedIn = this.permissionStore.signedIn();
      untracked(() => (signedIn ? this.start() : this.stop()));
    });
  }

  private start(): void {
    if (this.connection) {
      return;
    }

    // The auth cookie authenticates the connection, like every other admin request.
    const connection = new HubConnectionBuilder()
      .withUrl(HUB_URL, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(environment.production ? LogLevel.None : LogLevel.Warning)
      .build();

    connection.on(PERMISSIONS_UPDATED, (permissions: MyPermissions) => this.onPermissionsUpdated(permissions));
    // Automatic reconnect gave up (e.g. API restarted for longer than its retry window): keep trying while signed in.
    connection.onclose(() => this.scheduleRestart());

    this.connection = connection;
    this.connect();
  }

  private connect(): void {
    this.connection?.start().catch(() => this.scheduleRestart());
  }

  private scheduleRestart(): void {
    if (!this.connection || this.restartTimer) {
      return;
    }
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      if (this.connection?.state === HubConnectionState.Disconnected) {
        this.connect();
      }
    }, RESTART_DELAY_MS);
  }

  private stop(): void {
    if (this.restartTimer) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }
    const connection = this.connection;
    this.connection = null; // before stop(): onclose must not schedule a restart
    connection?.stop();
  }

  private onPermissionsUpdated(permissions: MyPermissions): void {
    this.permissionStore.set(permissions);
    this.leaveIfNoLongerAllowed();
  }

  /** Same rule as PermissionGuard, applied to every route on the path already open. */
  private leaveIfNoLongerAllowed(): void {
    const denied = this.activePath(this.router.routerState.snapshot.root).some((route) => {
      const data = route.routeConfig?.data as RoutePermissionData | undefined;
      return !!data?.menuKey && !this.permissionStore.can(data.menuKey, data.action ?? 'view');
    });

    if (denied) {
      this.router.navigateByUrl(this.permissionStore.firstAllowedRoute() ?? '/not-authorized');
    }
  }

  private activePath(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot[] {
    const path: ActivatedRouteSnapshot[] = [];
    for (let route: ActivatedRouteSnapshot | null = root; route; route = route.firstChild) {
      path.push(route);
    }
    return path;
  }
}
